/**
 * #1386: Org Detail's Kalshi/Polymarket panel reads. A non-2xx, network-failed
 * or unparseable read is unavailable, never an empty list; a valid empty array
 * stays authoritative and clears the panel. The #435 Trader Detail shape
 * (`unwrapManagerPolyFills` / `nextPolyFillsPanel`), kept beside this view.
 */

/** 'retained': the latest read failed and the panel still shows the last confirmed rows. */
export type OrgReadDisclosure = 'none' | 'retained' | 'unavailable';

export interface OrgReadPanel<T> {
  rows: T[];
  /** A valid read has landed for this org (now or before a retained failure). */
  confirmed: boolean;
  disclosure: OrgReadDisclosure;
}

export const unreadOrgPanel = <T>(): OrgReadPanel<T> => ({ rows: [], confirmed: false, disclosure: 'none' });
/** A failed read with nothing kept: the panel says it did not load. */
export const unavailableOrgPanel = <T>(): OrgReadPanel<T> => ({ rows: [], confirmed: false, disclosure: 'unavailable' });

/**
 * The org fills list, or null when the read cannot be trusted: a non-2xx, a
 * body that did not parse (`null`), no `fills` array, or an element that is
 * not an object. Only the shape is checked, never field values. Not
 * `parseFillsSnapshot`: that is the Kalshi fill contract, and this tape mixes
 * Kalshi and Polymarket rows. Orders use `parseOrdersSnapshot` (#238).
 */
export function decodeOrgFillRows(ok: boolean, payload: unknown): Array<Record<string, unknown>> | null {
  if (!ok || !payload || typeof payload !== 'object' || Array.isArray(payload)) return null;
  const rows = (payload as Record<string, unknown>).fills;
  if (!Array.isArray(rows)) return null;
  if (!rows.every((r) => r !== null && typeof r === 'object' && !Array.isArray(r))) return null;
  return rows as Array<Record<string, unknown>>;
}

/**
 * The panel after a read lands. `rows` is null for a failed read. Org Detail's
 * same-org Refresh keeps its rows until the new read lands (#437: retain only
 * where the surface already promises continuity), so a failure keeps the last
 * confirmed rows and says so; with nothing confirmed there is nothing to keep.
 * The org switch resets the panel before its reads, so this never carries
 * another org's rows. Org Detail uses this for orders and fills only; its
 * positions are never retained (see the core landing in index.tsx).
 */
export function nextOrgPanel<T>(prev: OrgReadPanel<T>, rows: T[] | null): OrgReadPanel<T> {
  if (rows) return { rows, confirmed: true, disclosure: 'none' };
  if (prev.confirmed) return { ...prev, disclosure: 'retained' };
  return unavailableOrgPanel();
}

/** A summary tile's count: only a confirmed current read is a number. */
export const orgPanelTileCount = (panel: OrgReadPanel<unknown>, count: number): string =>
  panel.confirmed && panel.disclosure === 'none' ? String(count) : '—';
